import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, useElements } from '@stripe/react-stripe-js';
import { useCheckoutViewModel } from '../viewmodels/useCheckoutViewModel';
import CheckoutSteps from './CheckoutSteps';
import ShippingForm from './ShippingForm';
import PaymentForm from './PaymentForm';
import OrderReview from './OrderReview';
import OrderSummary from './OrderSummary';
import ErrorBoundary from './ErrorBoundary';
import { fetchRuntimeConfig } from '../services/config';
import './Checkout.css';

/**
 * Keeps the Elements instance in sync with the current cart amount/currency.
 * Renders nothing — purely a side-effect component.
 */
const ElementsUpdater: React.FC<{ amount: number; currency: string }> = ({ amount, currency }) => {
  const elements = useElements();
  useEffect(() => {
    if (elements) {
      elements.update({ amount, currency });
    }
  }, [elements, amount, currency]);
  return null;
};

const Checkout: React.FC = () => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [initError, setInitError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isInitializing) {
        setInitError('Payment system initialization timeout. Please refresh the page.');
        setIsInitializing(false);
      }
    }, 10000); // 10 second timeout

    // Load Stripe key from runtime config
    fetchRuntimeConfig().then((config) => {
      const stripeKey = config.payment?.stripePublishableKey ||
                       import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
                       '';
      if (stripeKey) {
        const stripeAccountId = config.payment?.stripeAccountId;
        const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : undefined;
        setStripePromise(loadStripe(stripeKey, stripeOptions));
      }
      // When no Stripe key is available, stripePromise stays null.
      // PaymentForm handles this by auto-selecting manual payment.
      setIsInitializing(false);
      clearTimeout(timeout);
    }).catch((error: Error) => {
      console.error('Failed to load runtime config for Stripe:', error);
      // Fallback to env var
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
      if (stripeKey) {
        setStripePromise(loadStripe(stripeKey));
      }
      // Proceed without Stripe — PaymentForm handles null stripe gracefully
      setIsInitializing(false);
      clearTimeout(timeout);
    });

    return () => clearTimeout(timeout);
  }, [isInitializing]);

  const checkout = useCheckoutViewModel();

  // Show initialization error
  if (initError) {
    return (
      <div className="checkout-container">
        <div className="error">
          <h2>Payment System Error</h2>
          <p>{initError}</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      </div>
    );
  }

  // Show loading while Stripe is initializing
  if (isInitializing) {
    return (
      <div className="checkout-container">
        <div className="loading">Initializing payment system...</div>
      </div>
    );
  }

  // Show loading while cart is being fetched
  if (checkout.isCartLoading) {
    return (
      <div className="checkout-container">
        <div className="loading">Loading your cart...</div>
      </div>
    );
  }

  // Show error if cart failed to load
  if (checkout.cartError) {
    return (
      <div className="checkout-container">
        <div className="error">
          <h2>Error loading cart</h2>
          <p>Please try refreshing the page.</p>
        </div>
      </div>
    );
  }

  // Show empty cart message
  if (!checkout.cart || checkout.cart.items.length === 0) {
    return (
      <div className="checkout-container">
        <div className="empty-cart">
          <h2>Your cart is empty</h2>
          <p>Add some items to your cart before checking out.</p>
        </div>
      </div>
    );
  }

  const cart = checkout.cart;

  // Deferred intent options — PaymentElement renders without a client_secret.
  // Do NOT set capture_method here; it's configured per-payment-method via
  // payment_method_options on the backend PI to stay consistent with confirmation tokens.
  const elementsOptions = {
    mode: 'payment' as const,
    amount: cart.total,
    currency: cart.currency_code,
    appearance: { theme: 'stripe' as const },
  };

  const renderCheckoutContent = () => (
    <>
      <div className="checkout-header">
        <h1>Checkout</h1>
        <CheckoutSteps currentStep={checkout.currentStep} />
      </div>

      <div className="checkout-content">
        <div className="checkout-main">
          {checkout.errors.general && (
            <div className="error-banner">
              {typeof checkout.errors.general === 'string'
                ? checkout.errors.general
                : JSON.stringify(checkout.errors.general)}
            </div>
          )}

          {checkout.currentStep === 'shipping' && (
            <ShippingForm checkout={checkout} />
          )}

          {checkout.currentStep === 'payment' && (
            <PaymentForm checkout={checkout} />
          )}

          {checkout.currentStep === 'review' && (
            <OrderReview checkout={checkout} />
          )}
        </div>

        <div className="checkout-sidebar">
          <OrderSummary
            cart={cart}
            orderSummary={checkout.orderSummary}
            shippingMethod={checkout.checkoutData.shipping_method}
          />
        </div>
      </div>
    </>
  );

  return (
    <ErrorBoundary>
      <div className="checkout-container">
        {stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={elementsOptions}
            key={`${cart.total}-${cart.currency_code}`}
          >
            <ElementsUpdater amount={cart.total} currency={cart.currency_code} />
            {renderCheckoutContent()}
          </Elements>
        ) : (
          /* Fallback for manual-only payment (no Stripe key available) */
          renderCheckoutContent()
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Checkout;
